/**
 * The version handshake — cheap insurance for a live playtest.
 *
 * A player with a stale tab open is the most common failure mode of a
 * long-running server, and its symptom without this check is not an error
 * message: it is a client that connects, parses a snapshot slightly wrong, and
 * behaves strangely for twenty minutes before anyone works out why. Refusing
 * the join outright turns that into one sentence the player can act on.
 */
/**
 * Bumped whenever an intent, snapshot or log-event shape changes in a way an
 * older client cannot parse. Additive changes an old client can safely ignore
 * do not need a bump; anything renamed, removed or re-typed does.
 *
 * | Version | Shipped in | Change                                        |
 * | ------- | ---------- | --------------------------------------------- |
 * | 1       | M0-03      | first wire format: intents, snapshots, log     |
 * | 2       | M7-01      | join carries a player id; welcome returns it   |
 */
export const PROTOCOL_VERSION = 2;
/**
 * How long a player id may be, and the only shape one may take.
 *
 * A v4 UUID, spelled out rather than merely length-checked. The id is a bearer
 * credential until M7-01 lands real tokens, so the two things that matter about
 * it are that it is unguessable and that it is *bounded* — an unvalidated
 * identity string is a primary key an unfriendly client gets to choose, and a
 * megabyte of it is a row in the database either way.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function isPlayerId(value) {
    return typeof value === 'string' && UUID_PATTERN.test(value);
}
/**
 * The most a join token may be. A JWT is a few hundred bytes; this is the same
 * bound-the-input reflex `isPlayerId` follows — an unvalidated credential
 * string is a header an unfriendly client gets to choose the length of, and the
 * verifier should never be handed a megabyte to chew on.
 */
const MAX_TOKEN_LENGTH = 4096;
/**
 * The most a background id may be. A slug like `'herbalist'`; the bound is the
 * same bound-the-input reflex the token and player id follow, so the server's
 * catalogue lookup is never handed an unreasonable string to key on.
 */
const MAX_BACKGROUND_ID_LENGTH = 32;
/**
 * Compares a claimed protocol version against this build's.
 *
 * Returns a message rather than throwing because the caller is a Colyseus
 * `onAuth`, and the useful thing to do with a bad version is hand the player
 * the sentence — not a stack trace.
 */
export function checkProtocolVersion(claimed) {
    if (typeof claimed !== 'number' || !Number.isInteger(claimed)) {
        return {
            ok: false,
            message: 'This client did not say which version of the game it speaks. Please refresh the page.',
        };
    }
    if (claimed !== PROTOCOL_VERSION) {
        const direction = claimed < PROTOCOL_VERSION ? 'out of date' : 'newer than this world';
        return {
            ok: false,
            message: `This client is ${direction} (it speaks v${claimed}, the world speaks ` +
                `v${PROTOCOL_VERSION}). Please refresh the page.`,
        };
    }
    return { ok: true, version: claimed };
}
/**
 * Narrows an unvalidated join payload, version and identity included.
 *
 * `null` means the envelope was not a join request at all. A *missing or
 * malformed* player id is not that: it parses to `null` inside a perfectly
 * valid options object, because a first-time player has nothing to present and
 * a client sending rubbish should get a fresh identity rather than a locked
 * door. The locked door is M7-01's, and it will have a token to check.
 */
export function parseJoinOptions(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const options = value;
    if (typeof options.protocolVersion !== 'number')
        return null;
    if (typeof options.name !== 'string')
        return null;
    return {
        protocolVersion: options.protocolVersion,
        name: options.name,
        playerId: isPlayerId(options.playerId) ? options.playerId : null,
        token: typeof options.token === 'string' &&
            options.token.length > 0 &&
            options.token.length <= MAX_TOKEN_LENGTH
            ? options.token
            : null,
        backgroundId: typeof options.backgroundId === 'string' &&
            options.backgroundId.length > 0 &&
            options.backgroundId.length <= MAX_BACKGROUND_ID_LENGTH
            ? options.backgroundId
            : null,
    };
}
//# sourceMappingURL=version.js.map